import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import Spinner from '../Spinner/Spinner';
import './Table.scss';

const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

const Table = ({
  data = [],
  columns = [],
  searchPlaceholder = 'Search...',
  searchableFields = [],
  defaultSort,
  emptyMessage = 'No data available',
  isLoading = false,
  onRowClick,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState(defaultSort || { key: '', direction: SORT_DIRECTION.ASC });

  // Search functionality
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const fieldsToSearch = searchableFields.length > 0 ? searchableFields : columns.map((col) => col.id);

    return data.filter((item) =>
      fieldsToSearch.some((field) => {
        const value = item[field];
        return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, searchableFields, columns]);

  // Sort functionality
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison = 0;
      if (aValue > bValue) comparison = 1;
      if (aValue < bValue) comparison = -1;

      return sortConfig.direction === SORT_DIRECTION.DESC ? comparison * -1 : comparison;
    });
  }, [filteredData, sortConfig]);

  const handleSort = (columnId) => {
    const column = columns.find((col) => col.id === columnId);
    if (column?.sortable === false) return;

    setSortConfig((prevConfig) => {
      const isTogglingSort = prevConfig.key === columnId && prevConfig.direction === SORT_DIRECTION.ASC;
      return {
        key: columnId,
        direction: isTogglingSort ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
      };
    });
  };

  const getSortIcon = (columnId) => {
    const column = columns.find((col) => col.id === columnId);
    if (column?.sortable === false) return null;

    if (sortConfig.key !== columnId) {
      return <Icon iconId="chevron-up" className="sort-icon inactive" />;
    }

    const iconId = sortConfig.direction === SORT_DIRECTION.ASC ? 'chevron-up' : 'chevron-down';
    return <Icon iconId={iconId} className="sort-icon" />;
  };

  const renderCell = (row, column, index) => {
    if (column.template) {
      return column.template(row, index);
    }

    const value = row[column.id];
    const { className, showTooltip } = column;

    // Handle different data types
    if (value == null) return '';

    // Auto-format Date objects or cell-date className
    let valueToRender = value;
    if (className === 'cell-date' || value instanceof Date) {
      const dateValue = value instanceof Date ? value : new Date(value);
      valueToRender = dateValue.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } else if (className === 'cell-number') {
      valueToRender = Number(value).toLocaleString();
    } else if (typeof value === 'object' && value !== null) {
      // Convert any other object types to string to avoid React render errors
      valueToRender = value.toString();
    }

    if (showTooltip === true) {
      if (valueToRender == null) {
        valueToRender = value;
      }
      return (
        <Tooltip content={valueToRender} options={{ theme: 'grey' }}>
          <span>{valueToRender}</span>
        </Tooltip>
      );
    }

    return valueToRender;
  };

  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  return (
    <div className={`table-component ${className}`}>
      <Spinner show={isLoading} />

      <div className="search-bar">
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <Icon iconId="search" className="search-icon" />
        <span className="result-count">({filteredData.length})</span>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  onClick={() => handleSort(column.id)}
                  className={`
                    ${column.sortable !== false ? 'sortable' : ''} 
                    ${column.className || ''}
                  `}
                  style={{ width: column.width }}
                >
                  {column.title} {getSortIcon(column.id)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 && !isLoading ? (
              <tr className="empty-state-row">
                <td colSpan={columns.length} className="empty-state-cell">
                  <div className="empty-state-content">
                    {searchTerm ? `No results matching "${searchTerm}".` : emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr key={row.id || index} onClick={() => handleRowClick(row, index)} className="table-row">
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={`table-cell ${column.className || ''}`}
                      style={{ maxWidth: column.width }}
                    >
                      {renderCell(row, column, index)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      width: PropTypes.string,
      className: PropTypes.string,
      sortable: PropTypes.bool,
      showTooltip: PropTypes.bool,
      template: PropTypes.func,
    })
  ).isRequired,
  searchPlaceholder: PropTypes.string,
  searchableFields: PropTypes.arrayOf(PropTypes.string),
  emptyMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
  defaultSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf([SORT_DIRECTION.ASC, SORT_DIRECTION.DESC]),
  }),
};

export default Table;
